import { saveAs } from 'file-saver-es';
import { combineLatest, map, skip, tap } from 'rxjs';

import { Component, inject } from '@angular/core';

import { PermissionUtils, RouteFacade } from '@mitsubishi/core';
import { DatasheetFacade, DatasheetService, EditDatasheetRequest } from '@mitsubishi/project/data-access';
import { SpinnerService } from '@mitsubishi/ui/spinner';
import { MiscUtils } from '@mitsubishi/utils';

import { FieldWrapper, FormlyFieldConfig } from '@ngx-formly/core';

@Component({
  selector: 'apm-datasheet-section-wrapper',
  templateUrl: './datasheet-section-wrapper.component.html',
  styleUrls: ['./datasheet-section-wrapper.component.scss'],
  standalone: false,
})
export class DatasheetSectionWrapperComponent extends FieldWrapper {
  #routeFacade = inject(RouteFacade);
  #datasheetFacade = inject(DatasheetFacade);
  #spinnerService = inject(SpinnerService);
  #datasheetService = inject(DatasheetService);

  hasDatasheetMoreDetailsPermission = PermissionUtils.hasDatasheetMoreDetails();

  isEditing$ = this.#datasheetFacade.isEditing$.pipe(
    skip(1),
    tap(isEditing => {
      this.changeDisableOnAllFields(this.field, !isEditing);

      /* Trigger rebuild in order to re-run expressions */
      this.options.build?.();
    })
  );

  somethingEdited$ = this.#datasheetFacade.somethingEdited$;
  isSectionHidden$ = combineLatest([this.#datasheetFacade.selectedGroup$, this.#datasheetFacade.selectedSection$]).pipe(
    map(([groupId, sectionId]) => {
      return groupId !== this.field.props?.groupId || sectionId !== this.field.props?.sectionId;
    })
  );

  edit() {
    this.#datasheetFacade.setIsEditing(true);
  }

  calculate() {
    this.#datasheetFacade.addEditedValues(
      this.#datasheetFacade.editablePerformanceValues,
      this.#datasheetFacade.editableWorkingConditionsValues
    );
    this.#datasheetFacade.setIsEditing(false);
    this.#datasheetFacade.loadDatasheet(true);
  }

  cancel() {
    this.#datasheetFacade.removeEditedValues(
      this.#datasheetFacade.editablePerformanceKeys,
      this.#datasheetFacade.editableWorkingConditionsKeys
    );
    this.#datasheetFacade.setIsEditing(false);
    this.form.reset();
  }

  revert() {
    this.#datasheetFacade.setIsEditing(false);
    this.#datasheetFacade.loadDatasheet(false);
  }

  save() {
    this.#datasheetFacade.setIsEditing(false);
    this.#datasheetFacade.saveDatasheet();
  }

  changeDisableOnAllFields(field: FormlyFieldConfig, disabled: boolean) {
    if (field.props) {
      if (field.props.keepDisabled) {
        field.props.disabled = true;
        return;
      }
      field.props.disabled = disabled;
    }
    field.fieldGroup?.forEach(f => {
      this.changeDisableOnAllFields(f, disabled);
    });
  }

  downloadXls() {
    const projectId = this.#routeFacade.projectId;
    const unitId = this.#routeFacade.unitId;
    const performanceId = this.#routeFacade.performanceId;

    const editedPerformanceValues = this.#datasheetFacade.editablePerformanceValues;
    const editedWorkingConditionsValues = this.#datasheetFacade.editableWorkingConditionsValues;

    if (!unitId) {
      return;
    }

    const editedValues: EditDatasheetRequest = {
      performances: editedPerformanceValues,
      workingConditions: editedWorkingConditionsValues,
    };

    let req;
    this.#spinnerService.showMainSpinner();
    if (!performanceId) {
      req = this.#datasheetService.downloadPartialLoadsSelection(projectId, unitId, editedValues);
    } else {
      req = this.#datasheetService.downloadPartialLoadsStudy(projectId, performanceId, unitId, editedValues);
    }
    req.subscribe({
      next: resp => {
        const blob = new Blob([resp.body as Blob], { type: 'application/octet-stream;charset=UTF-8' });
        saveAs(blob, MiscUtils.extractFileNameFromHttpResponse(resp));
      },
      error: () => this.#spinnerService.hideMainSpinner(),
      complete: () => this.#spinnerService.hideMainSpinner(),
    });
  }
}
